import Card from '../../../components/card/Card';
import Spinner from '../../../components/Spinner/spinner';
import Button from '../../../components/button/Button';
import { useState } from 'react';
import PostBalanceSheet from './PostBalanceSheet';
import {
  getPostBalanceItems,
  updatePostBalanceItems,
  getBalanceSheetsByCuit,
  getBalanceInfoByCode,
} from '../../../services/RiskService';
import { Toast, useToast } from '../../../components/toast/toast';
import P from '../../../components/texts/p/P';
import { RISK_CODES, APPLICATION_STATUS } from '../../../utils/Constants';
import Modal from '../../../components/modal/modal';
import TextArea from '../../../components/textarea/TextArea';
import { updateApplication, getApplication } from '../../../services/ApplicationService';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../routes/routePaths';
import useStatus from '../../../hooks/useStatus';
import { toNumber, hasData } from '../../../utils/Utils';

interface PostBalanceActivityProps {
  applicationId: string | undefined;
  cuit: string | undefined;
  handleNextStep: () => void;
  handleRiskData: Function;
  purchasesSalesData: any;
  postBalanceData: any;
  setPostBalanceData: Function;
  applicationStatus: number;
  balanceLastMonth: any;
}

const PostBalanceActivity: React.FC<PostBalanceActivityProps> = ({
  applicationId,
  cuit,
  handleNextStep,
  handleRiskData,
  purchasesSalesData,
  postBalanceData,
  setPostBalanceData,
  applicationStatus,
  balanceLastMonth
}) => {
  const { isInCommittee } = useStatus({
    applicationStatus,
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const [purchasesData, setPurchasesData] = useState<any>({});
  const [salesData, setSalesData] = useState<any>({});
  const [validateForms, setValidateForms] = useState<boolean | null>(null);
  const [isCloudLoading, setIsCloudLoading] = useState<boolean>(false);
  const [cloudData, setCloudData] = useState<any>({});
  const navigate = useNavigate();
  const [salesValid, setSalesValid] = useState(false);
  const [purchasesValid, setPurchasesValid] = useState(false);

  const fetchPostBalanceItems = async () => {
    if (!cuit || !applicationId) {
      useToast.error({
        title: 'Error',
        id: 'post-balance-activity-toast',
        description: 'CUIT o ID de aplicación no disponible',
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    setIsCloudLoading(true);
    
    try {
      // Paso 1: Obtener el balanceSheetCode de la aplicación
      const applicationResponse = await getApplication(cuit, applicationId);
      
      if (!hasData(applicationResponse) || !applicationResponse.data) {
        useToast.error({
          title: 'Error al obtener la aplicación',
          id: 'post-balance-activity-toast',
          description: 'No se pudo obtener la información de la aplicación',
          closeButton: true,
          autoClose: 5000,
        });
        setIsCloudLoading(false);
        return;
      }

      const balanceSheetCode = applicationResponse.data.balance_sheet_code;
      let balance: any = null;

      // Paso 2: Intentar obtener el balance asociado a la aplicación
      if (balanceSheetCode) {
        try {
          const balanceByCodeResponse = await getBalanceInfoByCode(cuit, balanceSheetCode);
          if (hasData(balanceByCodeResponse) && balanceByCodeResponse.data) {
            balance = balanceByCodeResponse.data;
            console.log(`[PostBalanceActivity] Usando balance asociado a la aplicación: ${balanceSheetCode}, período: ${balance.from_year}-${balance.from_month} a ${balance.to_year}-${balance.to_month}`);
          }
        } catch (error) {
          console.warn(`[PostBalanceActivity] No se pudo obtener el balance ${balanceSheetCode}, usando fallback:`, error);
        }
      }

      // Paso 3: Fallback: usar el balance más reciente si no se encontró el asociado
      if (!balance) {
        const balancesResponse = await getBalanceSheetsByCuit(cuit);
        
        if (!hasData(balancesResponse) || !balancesResponse.data || balancesResponse.data.length === 0) {
          useToast.error({
            title: 'No hay balance cargado',
            id: 'post-balance-activity-toast',
            description: 'Debe cargar un balance antes de obtener las ventas post balance',
            closeButton: true,
            autoClose: 5000,
          });
          setIsCloudLoading(false);
          return;
        }

        balance = balancesResponse.data[0];
      }

      // Paso 4: Calcular el mes siguiente al balance
      const incrementMonth = (year: number, month: number) => {
        if (month === 12) {
          return { year: year + 1, month: '01' };
        } else {
          const newMonth = month + 1;
          return { year, month: newMonth < 10 ? `0${newMonth}` : `${newMonth}` };
        }
      };

      const { year, month } = incrementMonth(
        balance.to_year,
        balance.to_month
      );

      console.log(`[PostBalanceActivity] Calculando mes siguiente: balance termina en ${balance.to_year}-${balance.to_month}, mes siguiente: ${year}-${month}`);

      // Paso 5: Obtener las compras y ventas
      getPostBalanceItems(cuit, applicationId, String(year), month).then((response) => {
        if (response.status === 200 && hasData(response)) {
          setCloudData(response.data);
        } else {
          useToast.error({
            title: 'No hay ventas y compras posteriores al balance',
            id: 'post-balance-activity-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
        setIsCloudLoading(false);
      }).catch(() => {
        useToast.error({
          title: 'Error al obtener las compras y ventas',
          id: 'post-balance-activity-toast',
          description: 'No se pudo conectar con el servidor. Verifica tu conexión.',
          closeButton: true,
          autoClose: 5000,
        });
        setIsCloudLoading(false);
      });
    } catch (error) {
      console.error('[PostBalanceActivity] Error en fetchPostBalanceItems:', error);
      useToast.error({
        title: 'Error al obtener el balance',
        id: 'post-balance-activity-toast',
        description: 'No se pudo obtener la información del balance',
        closeButton: true,
        autoClose: 5000,
      });
      setIsCloudLoading(false);
    }
  };

  const saveData = (shouldContinue: boolean) => {
    if (
      !purchasesData.some((item) => item.month === null) &&
      !salesData.some((item) => item.month === null)
    ) {
      setIsDisabled(true);
      setIsSaving(true);
      
      const payload = { purchases: purchasesData, sales: salesData };
      
      updatePostBalanceItems(
        cuit,
        payload,
        applicationId
      ).then((response) => {
        if (response.status === 200) {
          useToast.success({
            title: 'Guardado con éxito',
            id: 'post-balance-activity-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });

          if (shouldContinue) {
            handleRiskData([RISK_CODES.bpb]);
            handleNextStep();
          }
        } else {
          useToast.error({
            title: 'Ocurrió un error al guardar la información.',
            id: 'post-balance-activity-toast',
            description: `Error ${response.status}: ${response.statusText || 'Error del servidor'}`,
            closeButton: true,
            autoClose: 5000,
          });
        }
        setPostBalanceData({
          purchases: purchasesData,
          sales: salesData,
          purchasesAnnualized: response.data?.purchasesAnnualized || 0,
          salesAnnualized: response.data?.salesAnnualized || 0,
          purchasesAverage: response.data?.purchasesAverage || 0,
          salesAverage: response.data?.salesAverage || 0,
        });
        setIsSaving(false);
        setIsDisabled(false);
      }).catch((error) => {
        useToast.error({
          title: 'Error de conexión',
          id: 'post-balance-activity-toast',
          description: 'No se pudo conectar con el servidor. Verifica tu conexión.',
          closeButton: true,
          autoClose: 5000,
        });
        setIsSaving(false);
        setIsDisabled(false);
      });
    } else {
      validateForms === null
        ? setValidateForms(true)
        : setValidateForms((prev) => !prev);
    }
  };

  const handleSheetData = (e, index) => {
    const data = e.values.map((item) => ({
      month: item.month,
      year: item.year,
      amount: toNumber(item.amount.value),
    }));
    if (index === 1) {
      setPurchasesData(data);
      setPurchasesValid(e.isValid);
    } else {
      setSalesData(data);
      setSalesValid(e.isValid);
    }
  };

  const canSubmit = salesValid && purchasesValid && !isDisabled && !isSaving;

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      }
      setIsSendingApplication(false);
    });
  };

  return (
    <Card spacing={{ left: '6' }}>
      <Toast id='post-balance-activity-toast' />
      <Toast id='application-toast' />
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />
      <Card
        display='flex'
        margin={{ bottom: '2', top: '4' }}
        justifyContent='space-between'
        alignItems='center'
      >
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Ventas posteriores al último balance
        </P>
        <Card width='230px'>
          <Button
            id='post-balance-activity-button-cloud'
            variant='secondary'
            onClick={fetchPostBalanceItems}
            icon='cloud_outline'
            iconAlign='right'
            fullWidth
            size='lg'
            disabled={isInCommittee || isCloudLoading}
          >
            {isCloudLoading ? (
              <Spinner size='sm' variant='primary' />
            ) : (
              'Buscar en la nube'
            )}
          </Button>
        </Card>
      </Card>
      <PostBalanceSheet
        onChangeSheet={(e) => handleSheetData(e, 0)}
        subtitle='Monto de ventas'
        totalText='Total ventas'
        validateForms={validateForms}
        cloudData={cloudData.sales}
        localData={postBalanceData.sales}
        disableFields={isInCommittee}
        showAllErrors={!canSubmit}
        balanceLastMonth={balanceLastMonth}
        inputMarginTop='8px'
      />
      <Card
        display='flex'
        margin={{ bottom: '2', top: '4' }}
        justifyContent='space-between'
        alignItems='center'
      >
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Compras posteriores al último balance
        </P>
      </Card>
      <PostBalanceSheet
        onChangeSheet={(e) => handleSheetData(e, 1)}
        subtitle='Monto de compras'
        totalText='Total compras'
        validateForms={validateForms}
        cloudData={cloudData.purchases}
        localData={postBalanceData.purchases}
        disableFields={isInCommittee}
        showAllErrors={!canSubmit}
        balanceLastMonth={balanceLastMonth}
        inputMarginTop='8px'
      />
      <Card display='flex' justifyContent='space-between' margin={{ top: '5' }}>
        <Card width='280px'>
          <Button
            id='checkRisk-button-reject'
            size='lg'
            variant='secondary'
            onClick={() => setIsRejectApplication(true)}
            fullWidth
            disabled={isSendingApplication}
          >
            {isSendingApplication ? (
              <Spinner size='sm' variant='primary' />
            ) : (
              'Declinar o solicitar cambios'
            )}
          </Button>
        </Card>
        <Card
          display='flex'
          justifyContent='flex-end'
          gap='16px'
          width={!isInCommittee ? '420px' : '210px'}
        >
          {!isInCommittee && (
            <Button
              disabled={!canSubmit}
              id='client-button-save'
              size='lg'
              variant='secondary'
              fullWidth
              onClick={() => {
                saveData(false);
              }}
            >
              {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
            </Button>
          )}
          <Button
            disabled={!canSubmit}
            id='client-button-save'
            size='lg'
            variant='primary'
            fullWidth
            onClick={() => {
              saveData(true);
            }}
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
          </Button>
        </Card>
      </Card>
    </Card>
  );
};

export default PostBalanceActivity;